import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  map,
  of,
  pairwise,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import {
  CancellationFlowStatus,
  isCancellationFlowOver,
} from 'src/app/_enums/cancellation/cancellation-flow-status.enum';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';

import { TaskDetailsService } from '../../task-details.service';

/**
 * The affaire's cancellations, details and all, read by the running
 * cancellation's cards, the cancellation cards and the log: one request per
 * load, on the mediator's loading edge — a refresh is a load too — and one per
 * change the sheet is told of outside a load. Provided by the sheet's body.
 */
@Injectable()
export class TaskCancellationsService {
  private readonly taskDetailsService = inject(TaskDetailsService);

  private readonly cancellationService = inject(CancellationService);

  private readonly toastr = inject(ToastrService);

  private readonly subject = new BehaviorSubject<
    CancellationFlowDetailsResponse[]
  >([]);

  /** Every cancellation of the affaire, whichever of its flows it ends; newest first. */
  readonly cancellations$ = this.subject.asObservable();

  /**
   * Those ending the opened flow, called off last; the answer's newest-first
   * order holds within each group. Rebuilt on each answer, not per read.
   */
  ofOpenedFlow: CancellationFlowDetailsResponse[] = [];

  /** The opened flow's cancellation still under way, if any: at most one is. */
  get running(): CancellationFlowDetailsResponse | undefined {
    return this.ofOpenedFlow.find(
      (cancellation) => !isCancellationFlowOver(cancellation.status)
    );
  }

  /** The flow the store is for; a late answer for another is dropped. */
  private flowId: string | null = null;

  /** A flow to read, or null to clear: either drops the read on its way. */
  private readonly fetch$ = new Subject<string | null>();

  constructor() {
    this.fetch$
      .pipe(
        switchMap((flowId) =>
          flowId
            ? this.cancellationService
                .getCancellationFlowByInsuranceFlow(flowId)
                .pipe(
                  map((res) => res.data ?? []),
                  // The cards are the only place these are managed, so a
                  // failure says so; a failed re-read keeps what it had.
                  catchError((err) => {
                    this.toastr.error(
                      getApiErrorMessage(
                        err,
                        $localize`Impossible de charger les résiliations de cette affaire`
                      )
                    );
                    return EMPTY;
                  })
                )
            : of([])
        ),
        takeUntilDestroyed()
      )
      .subscribe((cancellations) => this.set(cancellations));

    this.taskDetailsService.state$
      .pipe(
        map((state) => ({
          flowId: state.insuranceFlowData?.id ?? null,
          isLoading: state.isLoading,
        })),
        // The sheet starts out loading, so the first settled state is an edge too.
        startWith({ flowId: null, isLoading: true }),
        pairwise(),
        takeUntilDestroyed()
      )
      .subscribe(([previous, current]) => {
        // Another flow's cancellations go as soon as its data does — mid-load.
        // The same flow's stay up while re-read.
        if (current.flowId !== this.flowId) {
          this.flowId = current.flowId;
          this.fetch$.next(null);
        }
        if (current.flowId && previous.isLoading && !current.isLoading)
          this.fetch$.next(current.flowId);
      });

    // A change that comes with a refresh is read on the refresh's loading edge.
    this.taskDetailsService.changed$
      .pipe(
        filter(() => !!this.flowId && !this.taskDetailsService.state.isLoading),
        takeUntilDestroyed()
      )
      .subscribe(() => this.fetch$.next(this.flowId));
  }

  /**
   * A form's answer, shown at once; the lists behind the sheet show
   * cancellation statuses, so they are told too, which triggers the re-read.
   */
  changed(details: CancellationFlowDetailsResponse): void {
    this.replace(details);
    this.taskDetailsService.notifyChanged();
  }

  /**
   * Finalised, called off or deleted (no answer then): the affaire itself
   * reads differently, so it reloads. Reloading first, so the change is read
   * on the reload's loading edge — once.
   */
  settled(details?: CancellationFlowDetailsResponse): void {
    if (details) this.replace(details);
    this.taskDetailsService.refreshTaskDetails();
    this.taskDetailsService.notifyChanged();
  }

  private replace(details: CancellationFlowDetailsResponse): void {
    this.set(
      this.subject.value.map((cancellation) =>
        cancellation.id === details.id ? details : cancellation
      )
    );
  }

  private set(cancellations: CancellationFlowDetailsResponse[]): void {
    const opened = cancellations.filter(
      (cancellation) => cancellation.baseInsuranceFlow?.id === this.flowId
    );
    const calledOff = (cancellation: CancellationFlowDetailsResponse) =>
      cancellation.status === CancellationFlowStatus.CANCELLED;
    this.ofOpenedFlow = [
      ...opened.filter((cancellation) => !calledOff(cancellation)),
      ...opened.filter(calledOff),
    ];
    this.subject.next(cancellations);
  }
}
